import { useCallback, useEffect, useMemo, useState } from 'react'
import { ApiError } from '@/lib/apiClient'
import { useApiClient } from '@/hooks/useApiClient'

export type CommercialSignalKind = 'discovery' | 'commercial'
export type CommercialSignalState = 'active' | 'acknowledged' | 'snoozed' | 'dismissed' | 'resolved' | 'expired' | 'indeterminate'

export interface CommercialSignal {
  id: string
  rulesVersion: 'signals-v1'
  kind: CommercialSignalKind
  type: 'meeting_without_next_action' | 'reactivation' | 'p1_prolonged_inactivity' | 'm365_activity_without_structure' | 'missing_follow_up'
  state: CommercialSignalState
  severity: 'high' | 'medium' | 'low'
  confidence: 'high' | 'medium' | 'low' | 'indeterminate'
  organizationId: string
  organizationName: string
  personId: string | null
  initiativeId: string | null
  title: string
  explanation: string
  detectedAt: string
  lastObservedAt: string
  nextEligibleAt: string | null
  evidenceRefs: Array<{ id: string; date?: string | null; state?: string | null }>
  factors: Array<{ code: string; value?: unknown }>
  recommendedAction: { code: string; label: string }
  sources: Record<string, string>
}

type SignalStatus = 'loading' | 'ready' | 'not_signed_in' | 'not_configured' | 'error'

export function useCommercialSignals(filters: { organizationId?: string; personId?: string } = {}) {
  const { gate, makeClient } = useApiClient()
  const [signals, setSignals] = useState<CommercialSignal[]>([])
  const [status, setStatus] = useState<SignalStatus>('loading')
  const [error, setError] = useState<ApiError | Error | null>(null)
  const [attempt, setAttempt] = useState(0)
  const query = useMemo(() => {
    const params = new URLSearchParams()
    if (filters.organizationId) params.set('organizationId', filters.organizationId)
    if (filters.personId) params.set('personId', filters.personId)
    const value = params.toString()
    return value ? `?${value}` : ''
  }, [filters.organizationId, filters.personId])

  const reload = useCallback(() => setAttempt((value) => value + 1), [])

  useEffect(() => {
    if (gate !== 'ready') {
      setStatus(gate === 'not_signed_in' ? 'not_signed_in' : 'not_configured')
      return
    }
    const client = makeClient()
    if (!client) return
    const controller = new AbortController()
    setStatus('loading')
    setError(null)
    client.get<{ signals: CommercialSignal[] }>(`/api/commercial-signals${query}`, { signal: controller.signal })
      .then((response) => { setSignals(response.signals); setStatus('ready') })
      .catch((reason: ApiError | Error) => {
        if (controller.signal.aborted) return
        setError(reason); setStatus('error')
      })
    return () => controller.abort()
  }, [attempt, gate, makeClient, query])

  const transition = useCallback(async (id: string, body: Record<string, unknown>) => {
    const client = makeClient()
    if (!client) throw new ApiError('API_UNAUTHORIZED', 'Not signed in')
    const response = await client.patch<{ signal: CommercialSignal; verified: boolean }>(`/api/commercial-signals/${encodeURIComponent(id)}`, body)
    if (!response.verified) throw new Error('El cambio no pudo verificarse.')
    reload()
    return response.signal
  }, [makeClient, reload])

  const refresh = useCallback(async (cursor = 0) => {
    const client = makeClient()
    if (!client) throw new ApiError('API_UNAUTHORIZED', 'Not signed in')
    const response = await client.postOnce<{ status: 'success' | 'partial' | 'replayed'; verified: boolean; signals: CommercialSignal[]; sources?: Record<string, string> }>('/api/commercial-signals/refresh', { cursor })
    if (!response.verified) throw new Error('La actualización terminó sin evidencia verificable.')
    reload()
    return response
  }, [makeClient, reload])

  return { status, signals, error, reload, refresh, transition }
}
